// =============================
// File: src/Login.jsx (JSX)
// =============================
import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { persistAuthTokens } from "../../utils/authSession";

const API = process.env.REACT_APP_API_URL || "";
const API_LOGIN = `${API}/api/login`;

export function Login({ onLoginSuccess, sessionNotice = "" }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialNotice =
    searchParams.get("verified") === "1"
      ? "Correo verificado correctamente. Ya puedes iniciar sesion."
      : searchParams.get("verified") === "0"
        ? "El enlace de verificacion no es valido o ya expiro."
        : "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(sessionNotice);
  const [notice, setNotice] = useState(initialNotice);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");

  const persistSession = (data) => {
    persistAuthTokens(data);
    localStorage.setItem("token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);
    localStorage.setItem("user_id", data.user_id);
    localStorage.setItem("username", data.username);
    localStorage.setItem("email", data.email);
    localStorage.setItem("phone_number", data.phone_number);
    localStorage.setItem("role", data.role);
    localStorage.setItem("providerId", data.provider_id);
    localStorage.setItem("addresses", JSON.stringify(data.addresses || []));
    if (data.impersonated_by_user_id) {
      localStorage.setItem("impersonated_by_user_id", data.impersonated_by_user_id);
    } else {
      localStorage.removeItem("impersonated_by_user_id");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setUnverifiedEmail("");
    setLoading(true);
    try {
      const { data } = await axios.post(API_LOGIN, { email, password }, { withCredentials: true });
      persistSession(data);
      onLoginSuccess?.(data.user_id, data.username, data.email, data.phone_number, data.role, data.addresses, data.provider_id);
      navigate("/");
    } catch (err) {
      const msg = err?.response?.data?.message || "Credenciales invalidas";
      if (err?.response?.data?.code === "EMAIL_NOT_VERIFIED") {
        setUnverifiedEmail(err.response.data.email || email);
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const targetEmail = unverifiedEmail || email;
    if (!targetEmail) return;
    setError("");
    setNotice("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/resend-verification`, { email: targetEmail });
      setNotice(data?.message || "Si la cuenta existe, enviaremos un nuevo correo de verificacion.");
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo reenviar el correo de verificacion.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-sm border border-slate-200">
        <h1 className="text-2xl font-extrabold" style={{ color: "#B1336E" }}>Iniciar sesion</h1>
        <p className="mt-1 text-sm text-slate-600">Accede a tu panel para gestionar entregas.</p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="text-sm font-medium">Correo</label>
            <input
              type="email"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-200"
              placeholder="tucorreo@empresa.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium">Contrasena</label>
            <input
              type="password"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-200"
              placeholder="********"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {notice && <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 border border-emerald-200">{notice}</div>}
          {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-200">{error}</div>}
          {unverifiedEmail && (
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={loading}
              className="w-full rounded-xl border border-slate-300 px-4 py-2.5 font-semibold text-slate-700 disabled:opacity-60"
            >
              Reenviar correo de verificacion
            </button>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl px-4 py-2.5 font-semibold text-white shadow-sm disabled:opacity-60"
            style={{ background: "#B1336E" }}
          >
            {loading ? "Ingresando..." : "Ingresar"}
          </button>
        </form>

        <div className="mt-4 text-sm text-slate-600">
          No tienes cuenta?{" "}
          <Link to="/register" className="font-semibold underline underline-offset-4">Crear cuenta</Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
